import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

import type { UploadPhase } from "./media/upload";

export interface UploadProgress {
  done: number;
  total: number;
  name: string;
  phase: UploadPhase;
  ratio: number;
}

export interface ExportProgress {
  done: number;
  total: number;
}

interface MediaTasks {
  upload: UploadProgress | null;
  setUpload: (progress: UploadProgress | null) => void;
  exporting: ExportProgress | null;
  setExporting: (progress: ExportProgress | null) => void;
}

const MediaTasksContext = createContext<MediaTasks | null>(null);

/**
 * アップロード・まとめてダウンロードの進み具合。
 *
 * SPA なのでタブを移っても処理自体は走り続ける。進捗をアルバム画面の中に
 * 持つと、離れた途端に見えなくなり戻っても復元されないため、アプリ全体で持ち、
 * どの画面でもヘッダーの直下に出す(→ ScreenHeader)。
 */
export function useMediaTasks(): MediaTasks {
  const tasks = useContext(MediaTasksContext);
  if (tasks === null) throw new Error("MediaTasksProvider の外では使えません");
  return tasks;
}

export function MediaTasksProvider({ children }: { children: ReactNode }) {
  const [upload, setUpload] = useState<UploadProgress | null>(null);
  const [exporting, setExporting] = useState<ExportProgress | null>(null);

  // タブの行き来では処理が生きるが、タブを閉じる・再読み込みは処理ごと止まる。
  // どちらかが走っている間だけ、ブラウザの離脱確認を出す(文面はブラウザが決める)
  const running = upload !== null || exporting !== null;
  useEffect(() => {
    if (!running) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, [running]);

  return (
    <MediaTasksContext.Provider value={{ upload, setUpload, exporting, setExporting }}>
      {children}
    </MediaTasksContext.Provider>
  );
}
